import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import axios from 'axios';
import { Heart, CheckCircle, XCircle, Clock, DollarSign, CreditCard, Users, Calendar, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingStatusPage = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      const url = token 
        ? `${API}/bookings/${bookingId}/status?token=${token}`
        : `${API}/bookings/${bookingId}/status`;
      const response = await axios.get(url);
      setBooking(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [bookingId, token]);

  const handleApprove = async () => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }
    setActionLoading(true);
    try {
      window.location.href = `${API}/bookings/${bookingId}/quick-approve?token=${token}`;
    } catch (err) {
      toast.error('Failed to approve booking');
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }
    setActionLoading(true);
    try {
      window.location.href = `${API}/bookings/${bookingId}/quick-reject?token=${token}`;
    } catch (err) {
      toast.error('Failed to reject booking');
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { color: 'bg-amber-900/50 text-amber-400 border-amber-700', icon: Clock, label: 'Pending' },
      approved: { color: 'bg-teal-900/50 text-teal-400 border-teal-700', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-900/50 text-red-400 border-red-700', icon: XCircle, label: 'Rejected' }
    };
    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;
    return (
      <Badge className={`${variant.color} border flex items-center gap-1 text-sm px-3 py-1`}>
        <Icon className="h-4 w-4" />
        {variant.label}
      </Badge>
    );
  };

  const getPaymentBadge = (paymentStatus) => {
    const variants = {
      unpaid: { color: 'bg-orange-900/50 text-orange-400 border-orange-700', icon: CreditCard, label: 'Unpaid' },
      paid: { color: 'bg-emerald-900/50 text-emerald-400 border-emerald-700', icon: DollarSign, label: 'Paid' },
      expired: { color: 'bg-red-900/50 text-red-400 border-red-700', icon: XCircle, label: 'Expired' }
    };
    const variant = variants[paymentStatus] || variants.unpaid;
    const Icon = variant.icon;
    return (
      <Badge className={`${variant.color} border flex items-center gap-1 text-sm px-3 py-1`}>
        <Icon className="h-4 w-4" />
        {variant.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading booking status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center bg-slate-800 border-slate-700">
          <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-100 mb-2">Error</h2>
          <p className="text-slate-400 mb-4">{error}</p>
          <Button onClick={() => window.location.href = '/admin'} variant="outline" className="border-slate-600 text-white hover:bg-slate-700">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go to Admin Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <nav className="bg-slate-800 border-b border-slate-700">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl py-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
              <Heart className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-100">Booking Status</h1>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 md:px-6 max-w-3xl py-8">
        <Card className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-500 p-6 text-white">
            <h2 className="text-2xl font-bold mb-2">{booking.class_name}</h2>
            <p className="text-teal-100">Booking ID: {booking.id}</p>
          </div>

          {/* Status Section */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 mb-2">Booking Status</p>
                {getStatusBadge(booking.status)}
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-2">Payment Status</p>
                <div className="flex items-center gap-2">
                  {getPaymentBadge(booking.payment_status)}
                  {booking.woo_order_id && (
                    <span className="text-xs text-slate-500">Order #{booking.woo_order_id}</span>
                  )}
                </div>
              </div>
              <Button 
                onClick={fetchStatus} 
                variant="outline" 
                size="sm"
                className="rounded-full border-slate-600 text-white hover:bg-slate-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Details Section */}
          <div className="p-6 border-b border-slate-700">
            <h3 className="font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-teal-400" />
              Class Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-400">Date</p>
                <p className="font-medium text-slate-200">
                  {format(new Date(booking.start_time), 'MMMM d, yyyy')}
                </p>
              </div>
              <div>
                <p className="text-slate-400">Time</p>
                <p className="font-medium text-slate-200">
                  {format(new Date(booking.start_time), 'h:mm a')} - {format(new Date(booking.end_time), 'h:mm a')} PST
                </p>
              </div>
            </div>
          </div>

          {/* Students Section */}
          <div className="p-6 border-b border-slate-700">
            <h3 className="font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-400" />
              Students ({booking.student_count})
            </h3>
            <div className="space-y-2">
              {booking.students?.map((student, idx) => (
                <div key={idx} className="text-sm bg-slate-700 p-3 rounded-lg">
                  <p className="font-medium text-slate-200">
                    {student.first_name} {student.last_name}
                  </p>
                  <p className="text-slate-400">{student.email} • {student.phone}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Actions Section - Only show for pending bookings with token */}
          {booking.status === 'pending' && token && (
            <div className="p-6 bg-slate-700/50">
              <h3 className="font-semibold text-slate-100 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="bg-teal-500 hover:bg-teal-400 text-slate-900 rounded-full flex-1"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Booking
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={actionLoading}
                  variant="outline"
                  className="border-red-700 text-red-400 hover:bg-red-900/30 rounded-full flex-1"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject Booking
                </Button>
              </div>
            </div>
          )}

          {/* Already processed message */}
          {booking.status !== 'pending' && (
            <div className="p-6 bg-slate-700/50">
              <p className="text-center text-slate-400">
                This booking has been <strong className="text-slate-200">{booking.status}</strong>.
                {' '}
                <a href="/admin" className="text-teal-400 hover:underline">
                  Go to Admin Dashboard
                </a>
              </p>
            </div>
          )}
        </Card>

        {/* Auto-refresh notice */}
        <p className="text-center text-sm text-slate-500 mt-4">
          This page auto-refreshes every 30 seconds
        </p>
      </main>
    </div>
  );
};

export default BookingStatusPage;
